import axios from "axios";
import {
  Invoice,
  InvoiceFilters,
  ApiResponse,
  InvoiceSummary,
} from "../types/invoice";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
});

// Request interceptor for adding auth token and logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);

    // Add JWT token to headers if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error("API Response Error:", error);

    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      window.location.href = '/login';
      throw new Error("Sessão expirada. Por favor, faça login novamente.");
    }

    if (error.code === "ECONNABORTED") {
      throw new Error("Tempo limite de conexão excedido");
    }

    if (error.response) {
      // Server responded with error status
      const message =
        error.response.data?.error ||
        error.response.data?.message ||
        "Erro do servidor";
      throw new Error(message);
    } else if (error.request) {
      // Request was made but no response received
      throw new Error("Sem resposta do servidor. Verifique sua conexão.");
    } else {
      // Something else happened
      throw new Error(error.message || "Erro desconhecido");
    }
  }
);

// Export the configured axios instance for use in other services
export { api };

export class ApiService {
  /**
   * Get all invoices with optional filters
   */
  static async getInvoices(
    filters: InvoiceFilters = {}
  ): Promise<ApiResponse<Invoice[]>> {
    try {
      const response = await api.get("/invoices", { params: filters });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get a single invoice by ID
   */
  static async getInvoice(id: number): Promise<ApiResponse<Invoice>> {
    try {
      const response = await api.get(`/invoices/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new invoice
   */
  static async createInvoice(
    invoiceData: Partial<Invoice>
  ): Promise<ApiResponse<Invoice>> {
    try {
      const response = await api.post("/invoices", invoiceData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update an invoice
   */
  static async updateInvoice(
    id: number,
    updateData: Partial<Invoice>
  ): Promise<ApiResponse<Invoice>> {
    try {
      const response = await api.put(`/invoices/${id}`, updateData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete an invoice
   */
  static async deleteInvoice(id: number): Promise<ApiResponse<void>> {
    try {
      console.log(`🗑️ Attempting to delete invoice with ID: ${id}`);
      const response = await api.delete(`/invoices/${id}`);
      console.log(`✅ Delete response:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Delete error for invoice ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get invoice statistics
   */
  static async getInvoiceStats(
    filters: Partial<InvoiceFilters> = {}
  ): Promise<ApiResponse<InvoiceSummary[]>> {
    try {
      const response = await api.get("/invoices/summary/stats", {
        params: filters,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Export invoices to CSV
   */
  static async exportInvoicesCSV(filters: InvoiceFilters = {}): Promise<Blob> {
    try {
      const response = await api.get("/invoices/export/csv", {
        params: filters,
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Parse QR code without saving
   */
  static async parseQrCode(qrData: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/invoices/parse-qr", {
        qr_data: qrData,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Test API connection
   */
  static async testConnection(): Promise<boolean> {
    try {
      const response = await api.get("/health", { timeout: 5000 });
      return response.status === 200;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get the URL for an invoice photo
   */
  static getInvoicePhotoUrl(invoiceId: number): string {
    return `${API_BASE_URL}/invoices/${invoiceId}/photo`;
  }

  /**
   * Upload a photo for an invoice
   */
  static async uploadInvoicePhoto(
    invoiceId: number,
    file: File
  ): Promise<ApiResponse<Invoice>> {
    const formData = new FormData();
    formData.append("photo", file);
    const response = await api.post(`/invoices/${invoiceId}/photo`, formData, {
      headers: { "Content-Type": undefined },
      timeout: 60000,
    });
    return response.data;
  }

  /**
   * Delete an invoice photo
   */
  static async deleteInvoicePhoto(
    invoiceId: number
  ): Promise<ApiResponse<Invoice>> {
    try {
      const response = await api.delete(`/invoices/${invoiceId}/photo`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Postpone an expense to next month's report
   */
  static async postponeInvoice(id: number): Promise<ApiResponse<Invoice>> {
    try {
      const response = await api.post(`/invoices/${id}/postpone`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export default ApiService;
